import { useState } from "react";
import { Link } from "react-router-dom";
import HeatMeter from "@/components/base/HeatMeter";
import SectionHeading from "@/components/base/SectionHeading";
import TagBadge from "@/components/base/TagBadge";
import { hotTopics } from "@/mocks/topics";

const formatNumber = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)}万` : String(value);

export default function HotBoard() {
  const [activeId, setActiveId] = useState(hotTopics[0].id);
  const active = hotTopics.find((item) => item.id === activeId) || hotTopics[0];
  const maxHeat = hotTopics[0].heat;

  return (
    <section id="board" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-shell px-4 md:px-8">
        <SectionHeading
          eyebrow="LIVE BOARD"
          title="实时热榜：全网正在被传疯的话题"
          description="左侧为当前热度排名，点击任意一条，右侧会展示该话题的扩散轨迹与传播数据。热度越高，说明被谈论得越密集。"
          to="/ranking"
          toLabel="查看完整榜单"
        />

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ol className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-100">
              {hotTopics.slice(0, 10).map((topic, index) => {
                const isActive = topic.id === activeId;
                return (
                  <li key={topic.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(topic.id)}
                      aria-pressed={isActive}
                      className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                        isActive ? "bg-primary-500" : "hover:bg-background-200/70"
                      }`}
                    >
                      <span
                        className={`w-7 shrink-0 font-heading text-lg font-black ${
                          index < 3
                            ? isActive
                              ? "text-background-50"
                              : "text-primary-600"
                            : isActive
                              ? "text-background-50/80"
                              : "text-foreground-400"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="mt-0.5">
                        <TagBadge tag={topic.tag} />
                      </span>
                      <span
                        className={`flex-1 text-sm leading-snug ${
                          isActive ? "text-background-50" : "text-foreground-900"
                        }`}
                      >
                        {topic.title}
                      </span>
                      <span
                        className={`shrink-0 text-xs ${
                          isActive ? "text-background-100" : "text-foreground-500"
                        }`}
                      >
                        {formatNumber(topic.heat)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="lg:col-span-7">
            <article className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="border-b border-background-200 px-5 py-5 md:px-7 md:py-6">
                <div className="flex flex-wrap items-center gap-2">
                  <TagBadge tag={active.tag} />
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                    {active.category}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i
                      className={
                        active.trend === "up"
                          ? "ri-arrow-up-line text-primary-600"
                          : active.trend === "down"
                            ? "ri-arrow-down-line text-secondary-500"
                            : "ri-subtract-line text-foreground-400"
                      }
                    />
                    较上一小时 {active.trend === "down" ? "-" : "+"}
                    {active.trendValue}%
                  </span>
                </div>

                <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                  {active.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{active.summary}</p>
              </div>

              <div className="grid grid-cols-2 gap-px bg-background-200 md:grid-cols-4">
                <div className="bg-background-100 px-5 py-4">
                  <p className="text-xs text-foreground-500">综合热度</p>
                  <p className="mt-1.5 font-heading text-lg font-bold text-foreground-950">
                    {formatNumber(active.heat)}
                  </p>
                </div>
                <div className="bg-background-100 px-5 py-4">
                  <p className="text-xs text-foreground-500">累计转发</p>
                  <p className="mt-1.5 font-heading text-lg font-bold text-primary-600">
                    {formatNumber(active.shares)}
                  </p>
                </div>
                <div className="bg-background-100 px-5 py-4">
                  <p className="text-xs text-foreground-500">峰值时刻</p>
                  <p className="mt-1.5 font-heading text-sm font-bold text-foreground-950">
                    {active.peakTime}
                  </p>
                </div>
                <div className="bg-background-100 px-5 py-4">
                  <p className="text-xs text-foreground-500">热度水位</p>
                  <HeatMeter value={active.heat} max={maxHeat} className="mt-2.5" />
                </div>
              </div>

              <div className="flex flex-1 flex-col px-5 py-6 md:px-7">
                <p className="text-xs text-foreground-500">已扩散至</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {active.scope.map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-background-300 px-2.5 py-1 text-xs text-foreground-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                <p className="mt-5 rounded-md border-l-2 border-accent-500 bg-accent-50 px-4 py-3 text-sm leading-relaxed text-accent-900">
                  {active.highlight}
                </p>

                <h4 className="mt-6 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-timeline-view text-primary-600" />
                  扩散时间线
                </h4>
                <ol className="mt-4 space-y-4 border-l border-background-300 pl-5">
                  {active.timeline.map((point) => (
                    <li key={point.time} className="relative">
                      <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background-50 bg-primary-500" />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-sm font-bold text-foreground-950">
                          {point.time}
                        </span>
                        <span className="text-sm font-medium text-primary-600">{point.label}</span>
                        <span className="text-xs text-foreground-400">
                          热度 {formatNumber(point.heat)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                        {point.desc}
                      </p>
                    </li>
                  ))}
                </ol>

                <Link
                  to={`/topic/${active.id}`}
                  className="mt-6 inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看话题完整记录
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}